import { CartLinesUpdateEvent, CartErrorEvent, StandardEvents } from '@shopify/events';

/**
 * Lets a customer pick a DIFFERENT scrub for their 2nd (and, on the
 * "2 Gommages + 1 offert" tier, still-paid) item instead of being forced to
 * buy two units of the same scent. See snippets/tier-offer-selector.liquid /
 * assets/tier-offer-selector.js: when the customer chooses a different scrub
 * in the "2ème parfum" dropdown, the product form only adds 1 unit of the
 * current product and records the chosen variant as the cart-level attribute
 * "_tier_second_variant_id" on the add-to-cart submission.
 *
 * This script watches the cart (same event-driven pattern as
 * free-gift-bundle.js) and, whenever that attribute is present, adds 1 unit
 * of the chosen variant via a separate /cart/add.js call, then clears the
 * attribute so it isn't re-applied on every later cart change. The real
 * "-30% on the 2nd" and "2 achetés = 1 offert" automatic discounts already
 * apply across ANY combination of the 10 eligible products (they are
 * configured cart-wide, not per single product), so no other code needs to
 * change for mixed-scent bundles to price correctly.
 */

const SECOND_VARIANT_ATTRIBUTE = '_tier_second_variant_id';
const SECOND_PICK_PROPERTY_KEY = '_tier_second_pick';
const SOURCE_ID = 'second-scrub-bundle';

let processing = false;

async function fetchCart() {
  try {
    const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
}

function getSectionIds() {
  return [...document.querySelectorAll('cart-items-component')]
    .map((el) => el.dataset.sectionId)
    .filter(Boolean);
}

async function dispatchAndResolve(action, lines, request) {
  const deferred = CartLinesUpdateEvent.createPromise();
  document.dispatchEvent(new CartLinesUpdateEvent({ action, context: 'cart', lines, promise: deferred.promise }));

  try {
    const response = await request();
    if (response && response.status) {
      document.dispatchEvent(
        new CartErrorEvent({ error: response.description || response.message || 'Cart error', code: 'INVALID' })
      );
      deferred.reject(new Error(response.message || 'Cart error'));
      return;
    }

    const ajaxCart = await fetchCart();
    if (!ajaxCart) {
      deferred.reject(new Error('Unable to refresh cart'));
      return;
    }

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
      detail: {
        items: ajaxCart.items,
        source: SOURCE_ID,
        sourceId: SOURCE_ID,
        itemCount: ajaxCart.item_count,
        sections: response ? response.sections : undefined,
        didError: false,
      },
    });
  } catch (err) {
    deferred.reject(err);
  }
}

async function addSecondProduct(variantId) {
  await dispatchAndResolve(
    'add',
    [{ merchandiseId: `gid://shopify/ProductVariant/${variantId}`, quantity: 1 }],
    async () => {
      const formData = new FormData();
      formData.append('id', String(variantId));
      formData.append('quantity', '1');
      formData.append(`properties[${SECOND_PICK_PROPERTY_KEY}]`, 'true');
      const sectionIds = getSectionIds();
      if (sectionIds.length) formData.append('sections', sectionIds.join(','));

      return fetch('/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData,
      }).then((r) => r.json());
    }
  );
}

async function clearSecondVariantAttribute() {
  const cart = await fetchCart();
  if (!cart) return;
  const attributes = { ...(cart.attributes || {}) };
  if (!attributes[SECOND_VARIANT_ATTRIBUTE]) return;
  attributes[SECOND_VARIANT_ATTRIBUTE] = '';
  await fetch('/cart/update.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ attributes }),
  }).catch(() => {});
}

async function syncSecondProduct() {
  if (processing) return;
  processing = true;
  try {
    const cart = await fetchCart();
    if (!cart) return;

    const variantId = cart.attributes && cart.attributes[SECOND_VARIANT_ATTRIBUTE];
    if (!variantId) return;

    await addSecondProduct(variantId);
    await clearSecondVariantAttribute();
  } finally {
    processing = false;
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', syncSecondProduct);
} else {
  syncSecondProduct();
}

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  if (processing) return;
  event.promise?.then(() => syncSecondProduct()).catch(() => {});
});
